// Features: one laptop that shows the real app, feature by feature. It opens
// as the section scrolls in, the pointer tilts it gently, and each tab carries
// its screen in with a diagonal sweep of light.
import * as THREE from '../vendor/three/three.module.min.js';
import { createLaptop, createStudio, loadTexture } from './devices.js';

// Screens named here (not in the HTML) so build.mjs can inline them for file://.
const SCREENS = {
  es: {
    chain: 'assets/captures/web-chain-call.webp',
    strategy: 'assets/captures/web-strategy.webp',
    inst: 'assets/captures/crop-13f.webp',
    news: 'assets/captures/crop-news.webp',
    lesson: 'assets/captures/web-lesson.webp',
  },
  en: {
    chain: 'assets/captures/web-chain-call-en.webp',
    strategy: 'assets/captures/web-strategy-en.webp',
    inst: 'assets/captures/crop-13f-en.webp',
    news: 'assets/captures/crop-news-en.webp',
    lesson: 'assets/captures/web-lesson-en.webp',
  },
};
// Crops narrower than the display: shown as a zoomed-in part of the app, on
// the app's own background, instead of being stretched.
const ZOOMED = new Set(['inst', 'news']);

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const lerp = (a, b, t) => a + (b - a) * t;
const easeOut = (t) => 1 - (1 - clamp01(t)) ** 3;
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);
const band = (a, b, t) => {
  const x = clamp01((t - a) / (b - a));
  return x * x * (3 - 2 * x);
};

// the open laptop, three-quarter view, a little from above; `t` is how far the
// section has scrolled in (0 → 1), `hx`, `hy` the pointer over it (−1 → 1)
const LAPTOP = {
  target: new THREE.Vector3(0.05, 0.78, -0.12),
  dir: new THREE.Vector3(-0.1, 0.36, 1),
  fit: { w: 4.1, h: 3.7 },
  pose(d, t, hx, hy, time) {
    const e = easeOut(t);
    d.setOpen(lerp(0.28, 1.86, e));
    d.group.rotation.y = lerp(-0.7, -0.26, e) + hx * 0.14 + Math.sin(time * 0.0004) * 0.012;
    d.group.rotation.x = hy * 0.05;
    d.screenMaterial.uniforms.uBrightness.value = 0.03 + 0.97 * band(0.3, 0.8, t);
  },
};

export async function createShowcase(el, { lang = 'es', reduced = false, first = 'chain' } = {}) {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.setClearColor(0x000000, 0);

  const screens = SCREENS[lang] || SCREENS.es;
  const scene = new THREE.Scene();
  // neutral silver light, with a stronger rim so the aluminium edge reads on
  // the dark band
  await createStudio(renderer, scene, {
    rim: 0xe3e8ec,
    rimIntensity: 1.9,
    envIntensity: 0.95,
    keyIntensity: 2.1,
    fillIntensity: 0.8,
    fillColor: 0xeceff2,
  });

  const cache = new Map();
  const zoomed = (texture) => {
    const img = texture.image;
    const canvas = document.createElement('canvas');
    canvas.width = 1600;
    canvas.height = 1039; // the display's 1.54 : 1
    const g = canvas.getContext('2d');
    g.fillStyle = '#0d1116';
    g.fillRect(0, 0, canvas.width, canvas.height);
    const s = Math.min((canvas.width * 0.9) / img.width, (canvas.height * 0.88) / img.height);
    const w = img.width * s;
    const h = img.height * s;
    g.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
    const out = new THREE.CanvasTexture(canvas);
    out.colorSpace = THREE.SRGBColorSpace;
    out.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
    texture.dispose();
    return out;
  };
  const screenTexture = (key) => {
    if (!cache.has(key)) {
      cache.set(
        key,
        loadTexture(renderer, screens[key]).then((t) => (ZOOMED.has(key) ? zoomed(t) : t)),
      );
    }
    return cache.get(key);
  };

  const laptop = createLaptop({ screen: await screenTexture(first), body: 0xa3a9b1, shadowOpacity: 0.34 });
  scene.add(laptop.group);
  const uniforms = laptop.screenMaterial.uniforms;
  uniforms.uWipe.value = 1;
  const camera = new THREE.PerspectiveCamera(26, 1, 0.05, 60);
  const canvas = renderer.domElement;
  canvas.setAttribute('aria-hidden', 'true');
  el.appendChild(canvas);
  el.classList.add('is-3d');

  const state = { t: reduced ? 1 : 0, hx: 0, hy: 0, tx: 0, ty: 0, visible: false };
  let transition = null;
  let wanted = first;

  if (!reduced) {
    const zone = el.closest('[data-showcase]') || el;
    zone.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      state.tx = clamp01((e.clientX - r.left) / r.width) * 2 - 1;
      state.ty = clamp01((e.clientY - r.top) / r.height) * 2 - 1;
      wake();
    });
    zone.addEventListener('pointerleave', () => {
      state.tx = 0;
      state.ty = 0;
      wake();
    });
  }

  function frameCamera(aspect) {
    camera.aspect = aspect;
    const tan = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
    const dist = Math.max(LAPTOP.fit.w / (2 * tan * aspect), LAPTOP.fit.h / (2 * tan));
    camera.position.copy(LAPTOP.target).addScaledVector(LAPTOP.dir.clone().normalize(), dist);
    camera.lookAt(LAPTOP.target);
    camera.updateProjectionMatrix();
  }

  function draw(time) {
    const r = el.getBoundingClientRect();
    const w = Math.max(1, Math.round(r.width));
    const h = Math.max(1, Math.round(r.height));
    let moving = false;
    if (!reduced) {
      // 0 as the stage's top enters the screen, 1 when its middle is a little above centre
      const target = clamp01((innerHeight - r.top) / (innerHeight * 0.5 + r.height * 0.5));
      state.t += (target - state.t) * 0.09;
      state.hx += (state.tx - state.hx) * 0.08;
      state.hy += (state.ty - state.hy) * 0.08;
      moving =
        Math.abs(target - state.t) > 0.002 ||
        Math.abs(state.tx - state.hx) > 0.002 ||
        Math.abs(state.ty - state.hy) > 0.002;
    }
    if (transition) {
      const p = clamp01((time - transition.start) / transition.dur);
      uniforms.uMix.value = easeInOut(p);
      if (p >= 1) {
        uniforms.mapA.value = uniforms.mapB.value;
        uniforms.uMix.value = 0;
        transition = null;
      }
      moving = true;
    }
    LAPTOP.pose(laptop, state.t, state.hx, -state.hy, time);
    const size = renderer.getSize(new THREE.Vector2());
    if (size.x !== w || size.y !== h) renderer.setSize(w, h, false);
    frameCamera(w / h);
    renderer.render(scene, camera);
    return moving;
  }

  let raf = 0;
  function loop(time) {
    raf = 0;
    const moving = draw(time);
    // a gentle idle float while it is on screen, unless motion is reduced
    if (state.visible && (!reduced || moving)) raf = requestAnimationFrame(loop);
  }
  function wake() {
    if (!raf && state.visible) raf = requestAnimationFrame(loop);
  }

  // Shows a feature's screen. A newer request wins over one still loading.
  async function show(key) {
    if (!screens[key]) return;
    wanted = key;
    const texture = await screenTexture(key);
    if (wanted !== key || uniforms.mapA.value === texture) return;
    if (transition) uniforms.mapA.value = uniforms.mapB.value;
    uniforms.mapB.value = texture;
    uniforms.uMix.value = 0;
    transition = { start: performance.now(), dur: reduced ? 1 : 1150 };
    wake();
  }
  // warm the other screens once the first one is up
  Object.keys(screens).forEach((key) => screenTexture(key).catch(() => {}));

  new IntersectionObserver(
    ([entry]) => {
      state.visible = entry.isIntersecting;
      wake();
    },
    { rootMargin: '120px 0px' },
  ).observe(el);
  addEventListener('resize', wake);
  addEventListener('scroll', wake, { passive: true });
  return { show };
}
